import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { imagesApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import type { ImageMeta } from '../types/api';

/**
 * DeleteImageModal — confirm + destructive action for removing one image.
 * The backend's `DELETE /api/v1/images/:id` cascades to all boxes on the
 * image (via DB ON DELETE CASCADE on `ig_annotation_box.image_id`) and
 * removes the S3 blobs (original + thumbnail). No way back — the modal
 * surfaces the box count so the user knows what's about to vanish.
 */
interface DeleteImageModalProps {
    open: boolean;
    onClose: () => void;
    isDarkMode?: boolean;
    image: ImageMeta | null;
    /** Count of boxes currently on this image — shown in the confirm copy
     *  when > 0 so the user understands the scope of the delete. */
    boxCount?: number;
    orgId?: string;
    onDeleted: (id: string) => void;
}

export default function DeleteImageModal({
    open,
    onClose,
    isDarkMode = false,
    image,
    boxCount = 0,
    orgId,
    onDeleted,
}: DeleteImageModalProps) {
    const { t: tCommon } = useTranslation('common');
    const { t: tCanvas } = useTranslation('canvas');
    const { t: tErrors } = useTranslation('errors');
    const [submitting, setSubmitting] = useState(false);

    if (!image) return null;

    async function confirmDelete() {
        if (!image) return;
        setSubmitting(true);
        try {
            await imagesApi.remove(image.id, orgId);
            toast.success(tCanvas('image_deleted', { defaultValue: 'Image removed' }));
            onDeleted(image.id);
            onClose();
        } catch (err) {
            if (err instanceof APIClientError && err.code === 'project_locked') {
                toast.error(tErrors('project_locked'));
            } else {
                toast.error(tErrors('generic'));
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={submitting ? () => {} : onClose}
            title={tCanvas('delete_image_title', { defaultValue: 'Delete image' })}
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose} disabled={submitting}>
                        {tCommon('cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => void confirmDelete()}
                        disabled={submitting}
                    >
                        {submitting ? tCanvas('deleting', { defaultValue: 'Deleting…' }) : tCommon('delete')}
                    </Button>
                </>
            }
        >
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                {tCanvas('delete_image_body', {
                    defaultValue: 'Delete "{{name}}"? This removes the image bytes and {{count}} box(es) drawn on it. This cannot be undone.',
                    name: image.filename,
                    count: boxCount,
                })}
            </p>
            {boxCount > 0 && (
                <p className={cn('mt-3 text-sm rounded-lg border p-3',
                    isDarkMode ? 'bg-amber-900/20 border-amber-800 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800',
                )}>
                    {tCanvas('delete_image_warning', {
                        defaultValue: '{{count}} box(es) drawn on this image will be lost too.',
                        count: boxCount,
                    })}
                </p>
            )}
        </Modal>
    );
}
